import { useEffect, useRef, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Flex,
  Text,
  Box,
  Divider,
  VStack,
  HStack,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Select,
  Progress,
  FormLabel,
  useColorModeValue,
} from '@chakra-ui/react';

import { MdFolder } from 'react-icons/md';
import { AttachmentIcon, CloseIcon } from '@chakra-ui/icons';
import { useDispatch, useSelector } from 'react-redux';

import { updateFileSubmissionSubQuest } from 'store/modules/subquest/subquest-action';
import { ReducerStatus } from 'utils/constants';
import ResultModal from 'components/common/modals/ResultModal';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import SubQuestForm from 'components/subQuest/SubQuestForm';
import NumberInput from 'components/common/forms/NumberInput';
import useFileInput from 'hooks/useFileInput';
import { arrayOfString } from 'utils/jsonParser';
import { subquestActions } from 'store/modules/subquest/subquest-slice';
import { extractFileName } from 'utils/fileHelper';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import imageCompressor from 'quill-image-compress';

import {
  useQuillConfig,
  calculateContentSize,
} from 'utils/react-quill/quillConfig';
import RichTextSizeIndicator from 'utils/react-quill/RichTextSizeIndicator';
import FormikFormControl from 'components/common/forms/FormikFormControl';

ReactQuill.Quill.register('modules/imageCompress', imageCompressor);

function UpdateFileSubmissionMd() {
  const { modules, formats, style } = useQuillConfig();
  const dispatch = useDispatch();
  const bottomRef = useRef(null);

  // for current uploaded files
  const fileBgColor = useColorModeValue('blue.50', 'blue.700');
  const fileBorderColor = useColorModeValue('blue.200', 'blue.500');
  const fileTextColor = useColorModeValue('blue.700', 'white');

  // for new uploaded files
  const newFileBgColor = useColorModeValue('green.50', 'green.700');
  const newFileBorderColor = useColorModeValue('green.200', 'green.500');
  const newFileTextColor = useColorModeValue('green.700', 'white');

  const [triggerSubQuestForm, setTriggerSubQuestForm] = useState(0);
  const [isSubQuestFormValid, setIsSubQuestFormValid] = useState(false);
  const [isFormSubmit, setIsFormSubmit] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [subQuestFormInputs, setSubQuestFormInputs] = useState({
    name: '',
    objective: '',
    code: '',
    isPublished: '',
    tags: [],
    groups: [],
  });

  const [formInputs, setFormInputs] = useState({
    scoreType: 'Actual Score',
    totalScore: 100,
    passingScore: 60,
    requirements: '',
    answerSettings: 'One Time Submission',
  });
  const [contentSize, setContentSize] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filesToRemove, setFilesToRemove] = useState([]);

  const subQuest = useSelector((state) => state.subquest.subQuest);
  const questId = useSelector((state) => state.subquest.questId);
  const updateFileSubmission = useSelector(
    (state) => state.subquest.updateFileSubmission
  );
  const uploadProgress = useSelector((state) => state.subquest.uploadProgress);

  const {
    attachedFiles,
    fileError,
    fileInputRef,
    setFileError,
    handleAttachFile,
    removeAttachedFileFromFiles,
    clearAllFiles,
    handleFileInputChange,
  } = useFileInput({
    maxFileSizeMb: 30,
    fileType: 'application/x-zip-compressed',
    multiple: true,
  });

  const formikForm = useRef();
  const quillRef = useRef(null);

  const formValidation = Yup.object({
    scoreType: Yup.string().required('Required'),
    totalScore: Yup.number()
      .min(1, 'Total score must be at least 1')
      .required('Required'),
    passingScore: Yup.number()
      .min(0, 'Passing score cannot be negative')
      .max(Yup.ref('totalScore'), 'Passing score cannot exceed total score')
      .required('Required'),
    requirements: Yup.string()
      .test('not-empty', 'Requirements instructions are required', (value) => {
        if (!value) return false;
        const textContent = value.replace(/<[^>]*>/g, '').trim();
        return textContent.length > 0;
      })
      .test('file-size', 'Content size exceeds 5MB limit', (value) => {
        if (!value) return true;
        const calculatedSize = calculateContentSize(value);
        return calculatedSize <= 5;
      })
      .required('Requirements instructions are required'),
    answerSettings: Yup.string().required('Required'),
  });

  useEffect(() => {
    if (subQuest && updateFileSubmission.modal) {
      const { name, objective, code, isPublished, tags, ownerGroups } =
        subQuest;
      const {
        scoreType,
        totalScore,
        passingScore,
        requirements,
        answerSetting,
      } = subQuest.subQuestFileSubmission;

      setIsSubQuestFormValid(false);
      setSubQuestFormInputs({
        name,
        objective,
        code,
        isPublished: isPublished.toString(),
        tags: arrayOfString(tags),
        groups: arrayOfString(ownerGroups),
      });

      setFormInputs({
        scoreType,
        totalScore,
        passingScore,
        requirements,
        answerSettings: answerSetting,
      });

      setContentSize(calculateContentSize(requirements));
    }
  }, [subQuest, updateFileSubmission.modal]);

  useEffect(() => {
    if (updateFileSubmission.modal) {
      setUploadedFiles(subQuest?.subQuestFileSubmission?.filePaths || []);
    }
  }, [updateFileSubmission.modal, subQuest]);

  useEffect(() => {
    if (attachedFiles.length > 0) setFileError('');
  }, [attachedFiles, setFileError]);

  useEffect(() => {
    if (isFormSubmit && isSubQuestFormValid) {
      formikForm?.current?.submitForm();
      setIsFormSubmit(false);
    }
  }, [isFormSubmit, isSubQuestFormValid]);

  const handleToggleUpdateModal = (isOpen) => {
    if (!isOpen) {
      setFilesToRemove([]);

      dispatch(
        subquestActions.setUpdateFileSubmission({
          modal: false,
          id: null,
          status: ReducerStatus.IDLE,
          error: null,
        })
      );
      dispatch(subquestActions.resetSubQuestState());
    } else {
      dispatch(
        subquestActions.setUpdateFileSubmission({
          modal: isOpen,
        })
      );
    }
  };

  const handleRemoveUploadedFile = (fileToRemove) => {
    setFilesToRemove((prev) => [...prev, fileToRemove]);

    setUploadedFiles((prevFiles = []) =>
      prevFiles.filter((file) => file !== fileToRemove)
    );
  };

  const handleTriggerSubmit = () => {
    formikForm.current?.setFieldTouched('requirements', true, false);
    formikForm.current?.validateField('requirements');

    setTriggerSubQuestForm((trigger) => trigger + 1);
    setIsFormSubmit(true);
  };

  const handleSubmitForm = (values) => {
    if (isSubQuestFormValid === false) return;

    setPendingFormValues(values);
    setShowConfirmation(true);
  };

  const handleConfirmSubmission = () => {
    if (!pendingFormValues) return;

    setFormInputs({ ...pendingFormValues });

    const formData = new FormData();

    Object.entries(subQuestFormInputs).forEach(([key, value]) => {
      if (key !== 'tags' && key !== 'groups') formData.append(key, value);
    });

    if (subQuestFormInputs.tags.length > 0)
      subQuestFormInputs.tags.forEach((tag) => formData.append('Tags', tag));
    if (subQuestFormInputs.groups.length > 0)
      subQuestFormInputs.groups.forEach((group) =>
        formData.append('OwnerGroups', group)
      );

    Object.entries(pendingFormValues).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Files to remove
    if (filesToRemove && filesToRemove.length > 0) {
      filesToRemove.forEach((filePath) => {
        formData.append('FilesToRemove', filePath);
      });
    }

    // Existing files to keep
    if (uploadedFiles && uploadedFiles.length > 0) {
      uploadedFiles.forEach((filePath) => {
        formData.append('ExistingFilesToKeep', filePath);
      });
    }

    // Newly attached files
    if (attachedFiles && attachedFiles.length > 0) {
      attachedFiles.forEach((fileItem) => {
        if (fileItem.file) {
          let processedName = fileItem.file.name.trim();
          if (processedName.toLowerCase().endsWith('.zip')) {
            processedName = processedName.slice(0, -4);
          }

          formData.append('ZipFiles', fileItem.file, processedName);
        }
      });
    }

    dispatch(
      updateFileSubmissionSubQuest({
        subQuestId: subQuest?.id,
        questId,
        formData,
      })
    );

    setShowConfirmation(false);
    setPendingFormValues(null);

    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setPendingFormValues(null);
  };

  const handleConfirmationResult = () => {
    setShowConfirmation(false);
  };

  const handleResult = () => {
    dispatch(
      subquestActions.setUpdateFileSubmission({
        status: ReducerStatus.IDLE,
      })
    );

    if (updateFileSubmission.status === ReducerStatus.SUCCEEDED) {
      setFormInputs({
        scoreType: 'Actual Score',
        totalScore: 100,
        passingScore: 60,
        requirements: '',
        answerSettings: 'One Time Submission',
      });
      setSubQuestFormInputs({
        name: '',
        objective: '',
        code: '',
        isPublished: '',
        tags: [],
        groups: [],
      });
      setContentSize(0);
      setTriggerSubQuestForm(0);
      setIsSubQuestFormValid(false);
      clearAllFiles();
      setUploadedFiles([]);
      setFilesToRemove([]);
      setFileError('');

      dispatch(
        subquestActions.setUpdateFileSubmission({
          modal: false,
          id: null,
          status: ReducerStatus.IDLE,
          error: null,
        })
      );
    }
  };

  const getConfirmationDescription = () => {
    if (!pendingFormValues) return '';

    const hasNewTemplates = attachedFiles && attachedFiles.length > 0;
    const hasExistingFiles = uploadedFiles && uploadedFiles.length > 0;
    const hasFilesToRemove = filesToRemove && filesToRemove.length > 0;
    const scoreInfo =
      pendingFormValues.scoreType === 'Percentage'
        ? `${pendingFormValues.passingScore}% passing requirement rate`
        : `${pendingFormValues.passingScore}/${pendingFormValues.totalScore} points to pass`;

    const submissionType = pendingFormValues.answerSettings;

    return (
      <>
        <Text mb={3}>
          Are you sure you want to update this file submission sub-quest?
        </Text>
        <VStack align="start" spacing={1}>
          <Text>
            Sub-quest Name:{' '}
            <Text as="span" fontWeight="bold">
              {subQuestFormInputs.name}
            </Text>
          </Text>
          <Text>
            Score Requirements:{' '}
            <Text as="span" fontWeight="bold">
              {scoreInfo}
            </Text>
          </Text>
          <Text>
            Submission Type:{' '}
            <Text as="span" fontWeight="bold">
              {submissionType}
            </Text>
          </Text>
          {(hasExistingFiles || hasNewTemplates || hasFilesToRemove) && (
            <Box>
              <Text mb={1}>
                Template Files:{' '}
                <Text as="span" fontWeight="bold">
                  Changes to template files
                </Text>
              </Text>
              <VStack align="start" spacing={1} pl={4}>
                {hasExistingFiles && (
                  <>
                    <Text fontSize="sm" color="blue.600" fontWeight="medium">
                      Keeping {uploadedFiles.length} existing file(s):
                    </Text>
                    {uploadedFiles.map((filePath) => (
                      <Text key={uuidv4()} fontSize="sm" color="blue.600">
                        • {extractFileName(filePath)}
                      </Text>
                    ))}
                  </>
                )}
                {hasFilesToRemove && (
                  <>
                    <Text fontSize="sm" color="red.600" fontWeight="medium">
                      Removing {filesToRemove.length} file(s):
                    </Text>
                    {filesToRemove.map((filePath) => (
                      <Text key={uuidv4()} fontSize="sm" color="red.600">
                        • {extractFileName(filePath)}
                      </Text>
                    ))}
                  </>
                )}
                {hasNewTemplates && (
                  <>
                    <Text fontSize="sm" color="green.600" fontWeight="medium">
                      Adding {attachedFiles.length} new file(s):
                    </Text>
                    {attachedFiles.map((fileItem) => (
                      <Text key={fileItem.id} fontSize="sm" color="green.600">
                        • {fileItem.name}
                      </Text>
                    ))}
                  </>
                )}
              </VStack>
            </Box>
          )}
        </VStack>
      </>
    );
  };

  return (
    <Box>
      <ConfirmationActionModal
        isVisible={showConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmSubmission}
        onSubmitResult={handleConfirmationResult}
        status={updateFileSubmission.status}
        successMessage="The file submission sub-quest has been updated
        successfully."
        error={updateFileSubmission.error}
      >
        {getConfirmationDescription()}
      </ConfirmationActionModal>

      {updateFileSubmission.status === ReducerStatus.SUCCEEDED ||
      updateFileSubmission.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={updateFileSubmission.modal}
          onSubmit={handleResult}
          isError={updateFileSubmission.status === ReducerStatus.FAILED}
          title={
            updateFileSubmission.status === ReducerStatus.SUCCEEDED
              ? 'File Submission Sub-Quest Updated'
              : null
          }
          message={
            updateFileSubmission.status === ReducerStatus.SUCCEEDED
              ? 'The file submission sub-quest has been updated successfully.'
              : updateFileSubmission.error?.data?.title || 'Server Error'
          }
          errorObject={updateFileSubmission.error?.data?.errors}
        />
      ) : (
        <Modal
          size="3xl"
          isOpen={updateFileSubmission.modal}
          closeOnOverlayClick={
            updateFileSubmission.status !== ReducerStatus.LOADING
          }
          onClose={() => {
            handleToggleUpdateModal(false);
          }}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Update File Submission Sub-Quest</ModalHeader>
            <ModalCloseButton
              disabled={updateFileSubmission.status === ReducerStatus.LOADING}
            />
            <ModalBody my={3}>
              <SubQuestForm
                subQuestFormInputs={subQuestFormInputs}
                triggerSubQuestForm={triggerSubQuestForm}
                setSubQuestFormInputs={setSubQuestFormInputs}
                setIsSubQuestFormValid={setIsSubQuestFormValid}
              />
            </ModalBody>
            <Formik
              enableReinitialize
              innerRef={formikForm}
              initialValues={formInputs}
              onSubmit={(values) => {
                handleSubmitForm(values);
              }}
              validationSchema={formValidation}
            >
              {({ values, setFieldValue }) => (
                <Box>
                  <Form>
                    <ModalBody>
                      <Divider mb={4} />

                      <VStack spacing={6} align="stretch">
                        <Text fontSize="lg" fontWeight="bold">
                          File Submission Settings
                        </Text>

                        <Box>
                          <VStack spacing={3} align="stretch">
                            <HStack spacing={4} align="flex-start">
                              <Field name="scoreType">
                                {({ field, form }) => (
                                  <FormikFormControl
                                    label="Score Type"
                                    helperText="Compute scores as a percentage or as an actual score."
                                    errors={form.errors.scoreType}
                                    touched={form.touched.scoreType}
                                    flex={
                                      values.scoreType === 'Percentage'
                                        ? '3'
                                        : '2'
                                    }
                                  >
                                    <Select
                                      {...field}
                                      onChange={({ target: { value } }) => {
                                        setFieldValue('scoreType', value);
                                        if (value === 'Percentage') {
                                          setFieldValue('totalScore', 100);
                                        }
                                      }}
                                    >
                                      <option value="Actual Score">
                                        Actual Score
                                      </option>
                                      <option value="Percentage">
                                        Percentage
                                      </option>
                                    </Select>
                                  </FormikFormControl>
                                )}
                              </Field>

                              {values.scoreType !== 'Percentage' && (
                                <Field name="totalScore">
                                  {({ field, form }) => (
                                    <NumberInput
                                      label="Total Score"
                                      helperText="Maximum score for this sub-quest."
                                      errors={form.errors.totalScore}
                                      touched={form.touched.totalScore}
                                      flex="1"
                                      field={field}
                                      min={1}
                                      value={values.totalScore}
                                      onChange={(value) =>
                                        setFieldValue(
                                          'totalScore',
                                          parseInt(value, 10) || 0
                                        )
                                      }
                                    />
                                  )}
                                </Field>
                              )}

                              <Field name="passingScore">
                                {({ field, form }) => (
                                  <NumberInput
                                    label={
                                      values.scoreType === 'Percentage'
                                        ? 'Passing Percentage'
                                        : 'Passing Score'
                                    }
                                    helperText={
                                      values.scoreType === 'Percentage'
                                        ? 'Minimum percentage required to pass (0-100%)'
                                        : 'Minimum score required to pass'
                                    }
                                    flex="1"
                                    errors={form.errors.passingScore}
                                    touched={form.touched.passingScore}
                                    field={field}
                                    min={0}
                                    max={values.totalScore}
                                    value={values.passingScore}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'passingScore',
                                        parseInt(value, 10) || 0
                                      )
                                    }
                                  />
                                )}
                              </Field>
                            </HStack>
                          </VStack>
                        </Box>

                        <Field name="requirements">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.requirements &&
                                form.touched.requirements
                              }
                            >
                              <FormLabel>Requirements</FormLabel>
                              <Box position="relative" sx={style}>
                                <ReactQuill
                                  ref={quillRef}
                                  value={field.value}
                                  onChange={(content) => {
                                    const newContentSize =
                                      calculateContentSize(content);
                                    setContentSize(newContentSize);
                                    form.setFieldValue(field.name, content);
                                  }}
                                  onBlur={() => {
                                    form.setFieldTouched(field.name, true);
                                  }}
                                  placeholder="Describe what students need to submit..."
                                  style={{
                                    border:
                                      form.errors.requirements &&
                                      form.touched.requirements
                                        ? '2px solid #e53e3e'
                                        : '1px solid #e2e8f0',
                                    borderRadius: '6px',
                                  }}
                                  theme="snow"
                                  modules={modules}
                                  formats={formats}
                                />
                                <RichTextSizeIndicator size={contentSize} />
                              </Box>
                              <FormErrorMessage>
                                {form.errors.requirements}
                              </FormErrorMessage>
                              <FormHelperText>
                                Provide detailed submission requirements
                                including file formats, naming conventions,
                                deliverables, and any specific instructions
                                students need to follow.
                              </FormHelperText>
                            </FormControl>
                          )}
                        </Field>

                        <Field name="answerSettings">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.answerSettings &&
                                form.touched.answerSettings
                              }
                            >
                              <FormLabel>Submission Type</FormLabel>
                              <Select {...field}>
                                <option value="One Time Submission">
                                  One Time Submission
                                </option>
                                <option value="Multiple Submission">
                                  Multiple Submission
                                </option>
                                <option value="Overwrite Existing Session">
                                  Overwrite Existing Submission
                                </option>
                              </Select>
                              <FormErrorMessage>
                                {form.errors.answerSettings}
                              </FormErrorMessage>
                              <FormHelperText>
                                Control submission behavior: One Time (single
                                submission only), Multiple (unlimited attempts),
                                or Overwrite (replace previous submission)
                              </FormHelperText>
                            </FormControl>
                          )}
                        </Field>

                        <Box>
                          <Text fontSize="md" fontWeight="semibold" mb={3}>
                            Update Project Templates (Optional)
                          </Text>

                          {/* Display Current Uploaded Files */}
                          {uploadedFiles && uploadedFiles.length > 0 && (
                            <Box mb={6}>
                              <Text
                                fontSize="sm"
                                color="gray.700"
                                mb={3}
                                fontWeight="medium"
                              >
                                Current Template Files ({uploadedFiles.length}):
                              </Text>
                              <VStack spacing={2} align="stretch">
                                {uploadedFiles.map((filePath) => (
                                  <Flex
                                    key={uuidv4()}
                                    gap={3}
                                    p={3}
                                    bg={fileBgColor}
                                    borderRadius="md"
                                    border="1px"
                                    borderColor={fileBorderColor}
                                    align="center"
                                  >
                                    <MdFolder color="#3182ce" size={20} />
                                    <Text
                                      flex={1}
                                      fontSize="sm"
                                      color={fileTextColor}
                                    >
                                      {extractFileName(filePath)}
                                    </Text>
                                    <Button
                                      type="button"
                                      colorScheme="red"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleRemoveUploadedFile(filePath)
                                      }
                                    >
                                      <CloseIcon />
                                    </Button>
                                  </Flex>
                                ))}
                              </VStack>
                            </Box>
                          )}

                          {/* Display New Uploaded Files */}
                          <FormControl isInvalid={fileError !== ''}>
                            {attachedFiles.length > 0 && (
                              <Box my={4}>
                                <Text fontSize="sm" color="gray.600" mb={2}>
                                  New Template Files ({attachedFiles.length}):
                                </Text>
                                <VStack spacing={2} align="stretch">
                                  {attachedFiles.map((fileItem) => (
                                    <Flex
                                      key={fileItem.id}
                                      gap={3}
                                      p={3}
                                      bg={newFileBgColor}
                                      borderRadius="md"
                                      border="1px"
                                      borderColor={newFileBorderColor}
                                      align="center"
                                    >
                                      <MdFolder color="teal" size={20} />
                                      <Text flex={1} fontSize="sm">
                                        {fileItem.name}
                                      </Text>
                                      <Text
                                        fontSize="xs"
                                        color={newFileTextColor}
                                      >
                                        {(
                                          fileItem.size /
                                          (1024 * 1024)
                                        ).toFixed(2)}{' '}
                                        MB
                                      </Text>
                                      <Button
                                        type="button"
                                        colorScheme="red"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          removeAttachedFileFromFiles(
                                            fileItem.id
                                          )
                                        }
                                      >
                                        <CloseIcon />
                                      </Button>
                                    </Flex>
                                  ))}
                                </VStack>
                              </Box>
                            )}

                            <Box
                              as="button"
                              type="button"
                              borderRadius="md"
                              bg="teal"
                              w="100%"
                              mt={5}
                              px={4}
                              h={10}
                              color="white"
                              onClick={handleAttachFile}
                            >
                              <input
                                type="file"
                                hidden
                                accept=".zip"
                                multiple
                                ref={fileInputRef}
                                onChange={handleFileInputChange}
                              />
                              <AttachmentIcon mx={2} />
                              Add Project Template ZIP Files
                            </Box>
                            {fileError !== '' && (
                              <FormErrorMessage>{fileError}</FormErrorMessage>
                            )}
                            {uploadedFiles.length === 0 &&
                              attachedFiles.length === 0 && (
                                <Text mt={2} fontSize="sm" color="gray.600">
                                  No template files currently uploaded. Add new
                                  files above to provide project templates for
                                  students.
                                </Text>
                              )}
                            <FormHelperText>
                              Upload ZIP files containing project templates,
                              starter code, or reference materials for students.
                              Maximum file size per file: 30MB. Supported
                              format: .zip only. You can select multiple files
                              at once.
                            </FormHelperText>
                          </FormControl>
                        </Box>

                        {updateFileSubmission.status ===
                          ReducerStatus.LOADING && (
                          <Progress
                            hasStripe
                            isAnimated
                            value={uploadProgress}
                          />
                        )}
                      </VStack>
                    </ModalBody>

                    <ModalFooter ref={bottomRef}>
                      <Button
                        variant="outline"
                        colorScheme="blue"
                        mr={3}
                        isLoading={
                          updateFileSubmission.status === ReducerStatus.LOADING
                        }
                        onClick={() => {
                          handleToggleUpdateModal(false);
                        }}
                      >
                        Close
                      </Button>
                      <Button
                        isLoading={
                          updateFileSubmission.status === ReducerStatus.LOADING
                        }
                        onClick={handleTriggerSubmit}
                        colorScheme="red"
                        px={10}
                      >
                        Update
                      </Button>
                    </ModalFooter>
                  </Form>
                </Box>
              )}
            </Formik>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default UpdateFileSubmissionMd;
